/**
 * Botón «Instalar Donna» (ícono en el escritorio o en la pantalla de inicio) y la guía paso a paso para los equipos
 * donde el navegador no tiene botón propio (iPhone y iPad con Safari, Mac con Safari, algunos Android).
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useInstalar, type Equipo } from "../pwa.ts";

const COMPARTIR = (
  <svg className="ico-paso" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-label="Compartir" role="img">
    <path d="M12 3v12M8 7l4-4 4 4" /><path d="M6 11H5a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-8a1 1 0 0 0-1-1h-1" />
  </svg>
);
const MAS = (
  <svg className="ico-paso" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-label="Agregar" role="img">
    <rect x="4" y="4" width="16" height="16" rx="3" /><path d="M12 8v8M8 12h8" />
  </svg>
);
const MENU = <b className="mono" aria-label="menú">⋮</b>;
export const ICONO_INSTALAR = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="6" y="2" width="12" height="20" rx="2.5" /><path d="M12 7v7M9 11l3 3 3-3" /><path d="M10.5 18.5h3" />
  </svg>
);

interface Guia { titulo: string; pasos: ReactNode[]; nota?: string }
const GUIAS: Record<Exclude<Equipo, "instalada" | "boton">, Guia> = {
  iphone: {
    titulo: "iPhone",
    pasos: [<>Abre Donna en <b>Safari</b>.</>, <>Toca <b>Compartir</b> {COMPARTIR} en la barra de abajo.</>, <>Desliza hacia abajo y toca <b>Agregar a pantalla de inicio</b> {MAS}.</>, <>Toca <b>Agregar</b>. El ícono de Donna queda junto a tus apps.</>],
    nota: "En Chrome para iPhone también se puede: el botón Compartir está arriba, a la derecha de la dirección.",
  },
  ipad: {
    titulo: "iPad",
    pasos: [<>Abre Donna en <b>Safari</b>.</>, <>Toca <b>Compartir</b> {COMPARTIR} arriba, a la derecha de la dirección.</>, <>Toca <b>Agregar a pantalla de inicio</b> {MAS}.</>, <>Toca <b>Agregar</b>. Donna se abrirá a pantalla completa, como una app.</>],
  },
  "mac-safari": {
    titulo: "Mac con Safari",
    pasos: [<>En la barra de menú, abre <b>Archivo</b>.</>, <>Elige <b>Agregar al Dock</b>.</>, <>Pulsa <b>Agregar</b>. Donna queda en el Dock y en Aplicaciones.</>],
    nota: "Necesita macOS Sonoma o más reciente. También puedes instalarla desde Google Chrome o Microsoft Edge.",
  },
  android: {
    titulo: "Android",
    pasos: [<>Abre Donna en <b>Google Chrome</b>.</>, <>Toca el menú {MENU} arriba a la derecha.</>, <>Toca <b>Instalar aplicación</b> o <b>Agregar a la pantalla principal</b>.</>, <>Confirma con <b>Instalar</b>.</>],
    nota: "En Samsung Internet: menú ≡ → Agregar página a → Pantalla de inicio.",
  },
  otro: {
    titulo: "Computadora (Windows o Mac)",
    pasos: [<>Abre Donna en <b>Google Chrome</b> o <b>Microsoft Edge</b>.</>, <>Pulsa el ícono de instalar que aparece al final de la barra de direcciones (una pantalla con una flecha).</>, <>Pulsa <b>Instalar</b>. Donna queda en el escritorio y en el menú Inicio.</>],
    nota: "Firefox no permite instalar aplicaciones web; usa Chrome o Edge.",
  },
};

export function GuiaInstalar({ equipo, alCerrar }: { equipo: Equipo; alCerrar: () => void }) {
  const actual = equipo === "instalada" || equipo === "boton" ? "otro" : equipo;
  const ref = useRef<HTMLButtonElement>(null);
  useEffect(() => { ref.current?.focus(); }, []);
  const g = GUIAS[actual];
  const otras = (Object.keys(GUIAS) as (keyof typeof GUIAS)[]).filter((k) => k !== actual);
  return (
    <div className="modal-fondo" role="dialog" aria-modal="true" aria-labelledby="t-instalar" onKeyDown={(e) => { if (e.key === "Escape") alCerrar(); }}
      onClick={(e) => { if (e.target === e.currentTarget) alCerrar(); }}>
      <section className="card guia-instalar">
        <div className="fila" style={{ justifyContent: "space-between", alignItems: "center" }}>
          <h2 id="t-instalar" style={{ margin: 0 }}>Instalar Donna en tu {g.titulo}</h2>
          <button ref={ref} className="btn chico" onClick={alCerrar}>Cerrar</button>
        </div>
        <p className="muted">Quedará un ícono de Donna en la pantalla de inicio o en el escritorio, y se abrirá a pantalla completa, sin la barra del navegador.</p>
        <ol className="pasos-instalar">{g.pasos.map((p, i) => <li key={i}>{p}</li>)}</ol>
        {g.nota && <p className="muted chico-txt">{g.nota}</p>}
        <details>
          <summary>Pasos para otros equipos</summary>
          {otras.map((k) => (
            <div key={k} className="otra-guia">
              <h3>{GUIAS[k].titulo}</h3>
              <ol className="pasos-instalar">{GUIAS[k].pasos.map((p, i) => <li key={i}>{p}</li>)}</ol>
            </div>
          ))}
        </details>
        <p className="muted chico-txt">Para instalarla en otro equipo, abre <b className="mono">{location.host}</b> en ese equipo y sigue los mismos pasos. Donna necesita internet para trabajar.</p>
      </section>
    </div>
  );
}

/** Botón para instalar: usa el aviso del navegador si existe; si no, muestra la guía. No aparece si Donna ya está instalada. */
export function BotonInstalar({ variante = "cabecera" }: { variante?: "cabecera" | "login" }) {
  const { equipo, instalar } = useInstalar();
  const [guia, setGuia] = useState(false);
  if (equipo === "instalada") return null;
  const pulsar = async () => { if (!(await instalar())) setGuia(true); };
  return (
    <>
      {variante === "cabecera"
        ? <button type="button" className="btn chico btn-instalar" onClick={pulsar} title="Instalar Donna en este equipo" aria-label="Instalar Donna en este equipo">{ICONO_INSTALAR}<span>Instalar</span></button>
        : <button type="button" className="btn btn-instalar-login" onClick={pulsar}>{ICONO_INSTALAR}Instalar Donna en este equipo</button>}
      {guia && <GuiaInstalar equipo={equipo} alCerrar={() => setGuia(false)} />}
    </>
  );
}
